import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { clearLeadCache } from '../lib/leadCache';
import {
  APP_ACCOUNT_KIND,
  clearPendingSignupAccountOptions,
  completeNewAccountSetup,
  completeSetupFromUserMetadata,
  setPendingSignupAccountOptions,
} from '../lib/accountSetup';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const signupSetupPromiseRef = React.useRef(null);
  const setupByUserRef = React.useRef(new Map());

  useEffect(() => {
    let mounted = true;

    // Account setup only needs to succeed once per signed-in user; auth events (token refresh,
    // tab refocus) fire often and re-running it each time slows and destabilises loading.
    const ensureAccountSetup = (sessionUser) => {
      const existing = setupByUserRef.current.get(sessionUser.id);
      if (existing) return existing;
      const run = completeSetupFromUserMetadata(sessionUser).catch((e) => {
        setupByUserRef.current.delete(sessionUser.id);
        throw e;
      });
      setupByUserRef.current.set(sessionUser.id, run);
      return run;
    };

    const applySession = async (sessionUser) => {
      if (!mounted) return;
      if (signupSetupPromiseRef.current) {
        try {
          await signupSetupPromiseRef.current;
        } catch (e) {
          console.error('[Auth] signup setup failed:', e);
        }
      }
      if (sessionUser) {
        try {
          await ensureAccountSetup(sessionUser);
        } catch (e) {
          console.error('[Auth] account setup from metadata failed:', e);
          if (typeof window !== 'undefined') {
            window.__tradelynxAccountSetupError = e?.message || String(e);
          }
        }
      }
      if (!mounted) return;
      if (!sessionUser) setupByUserRef.current.clear();
      setUser(sessionUser);
      setLoading(false);
    };

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => applySession(session?.user ?? null))
      .catch((e) => {
        console.error('[Auth] getSession failed:', e);
        if (mounted) setLoading(false);
      });

    // Supabase holds its auth lock while this callback runs. Calling Supabase from inside it can
    // dead-lock every later request, so defer the work until the callback has returned.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setTimeout(() => applySession(session?.user ?? null), 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session?.user) {
      try {
        await completeSetupFromUserMetadata(data.session.user);
      } catch (e) {
        if (typeof window !== 'undefined') {
          window.__tradelynxAccountSetupError = e?.message || String(e);
        }
        throw e;
      }
    }
    return data;
  };

  const signup = async (email, password, accountOptions = {}) => {
    const accountKind = accountOptions.accountKind || APP_ACCOUNT_KIND.DASHBOARD;
    const crmRole = accountOptions.crmRole;
    const crmJoinCode = accountOptions.crmJoinCode;

    const userMetadata = {
      app_account_type: accountKind,
      account_setup_complete: false,
    };
    if (accountKind === APP_ACCOUNT_KIND.CRM) {
      userMetadata.crm_role = crmRole;
      userMetadata.crm_join_code = String(crmJoinCode || '').trim();
    }

    const pendingOptions = {
      accountKind,
      crmRole,
      crmJoinCode,
      email,
    };
    setPendingSignupAccountOptions(pendingOptions);

    const runSignupSetup = async () => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: userMetadata },
      });
      if (error) throw error;

      if (data.session?.user) {
        await completeNewAccountSetup(data.session.user, pendingOptions);
        await supabase.auth.updateUser({
          data: { ...userMetadata, account_setup_complete: true },
        });
        clearPendingSignupAccountOptions();
      }

      return data;
    };

    signupSetupPromiseRef.current = runSignupSetup();
    try {
      return await signupSetupPromiseRef.current;
    } finally {
      signupSetupPromiseRef.current = null;
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    await clearLeadCache();
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
